import classNames from "classnames";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type TextareaHTMLAttributes
} from "react";
import type { TextAreaInputHandle } from "./textAreaInputHandle";

interface TextAreaInputProps {
  id?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  title?: string;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  className?: string;
  containerClassName?: string;
  rows?: number;
  cols?: number;
  onValueChanged?: (value: string) => void;
  onBlur?: () => void;
  additionalAttributes?: TextareaHTMLAttributes<HTMLTextAreaElement>;
}

export const TextAreaInput = forwardRef<TextAreaInputHandle, TextAreaInputProps>(
  (
    {
      id,
      name,
      value = "",
      placeholder,
      title,
      disabled = false,
      required = false,
      invalid = false,
      className = "",
      containerClassName = "",
      rows = 4,
      cols,
      onValueChanged,
      onBlur,
      additionalAttributes
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState<string>(value);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
      setInputValue(value);
    }, [value]);

    const handleInputChange = useCallback(
      (event: ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = event.target.value;
        setInputValue(newValue);

        if (onValueChanged) {
          onValueChanged(newValue);
        }
      },
      [onValueChanged]
    );

    const handleBlur = useCallback(() => {
      if (onBlur) {
        onBlur();
      }
    }, [onBlur]);

    const focusAsync = useCallback(() => {
      if (textAreaRef.current) {
        textAreaRef.current.focus();
      }
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        focus: focusAsync,
        focusAsync: focusAsync
      }),
      [focusAsync]
    );

    return (
      <div
        className={classNames(
          "rounded-tl-br-md p-xs relative border border-neutral-300",
          "transition-short-in-out hover:border-dark-blue",
          "focus-outline has-[textarea:focus-visible]:outline",
          "[&>textarea]:outline-none",
          {
            "input--error !border-error-200 has-[textarea:focus-visible]:outline-error-200/75":
              invalid
          },
          containerClassName
        )}
      >
        <textarea
          ref={textAreaRef}
          id={id}
          name={name}
          value={inputValue}
          placeholder={placeholder}
          title={title}
          disabled={disabled}
          required={required}
          rows={rows}
          cols={cols}
          className={classNames(
            "w-full p-sm border-0 rounded-tl-br-xs text-t-sm",
            "focus-visible:outline-0 resize-none",
            {
              "!bg-neutral-100/30 !border-neutral-400/30 cursor-not-allowed": disabled,
              "!placeholder:text-neutral-400/70 !text-neutral-600/70": disabled,
              "!border-error-200 !focus:border-error-200 !focus:ring-error-200": invalid
            },
            className
          )}
          onChange={handleInputChange}
          onBlur={handleBlur}
          data-f-datainput="true"
          {...additionalAttributes}
        />
      </div>
    );
  }
);

TextAreaInput.displayName = "TextAreaInput";

export default TextAreaInput;
export type { TextAreaInputHandle };
